/**
 * Tuple vector math. The actual arithmetic is delegated to gl-matrix's proven,
 * tested `vec3` (standardized, not hand-rolled); these thin wrappers preserve the
 * codebase's immutable tuple API (gl-matrix is mutating/out-param) so every call
 * site and the unit tests stay unchanged.
 */
import { vec3 } from 'gl-matrix';
import type { Vec3 } from './types';

const out = (): Vec3 => [0, 0, 0];

export const sub = (a: Vec3, b: Vec3): Vec3 => vec3.subtract(out(), a, b) as Vec3;
export const add = (a: Vec3, b: Vec3): Vec3 => vec3.add(out(), a, b) as Vec3;
export const scale = (a: Vec3, s: number): Vec3 => vec3.scale(out(), a, s) as Vec3;
export const cross = (a: Vec3, b: Vec3): Vec3 => vec3.cross(out(), a, b) as Vec3;
export const dot = (a: Vec3, b: Vec3): number => vec3.dot(a, b);
export const length = (a: Vec3): number => vec3.length(a);

/**
 * Normalize, returning [0,0,0] below an epsilon length. (gl-matrix's
 * vec3.normalize would instead amplify a sub-epsilon vector to unit length.)
 */
export const normalize = (a: Vec3): Vec3 => {
  const len = vec3.length(a);
  return len > 1e-12 ? (vec3.scale(out(), a, 1 / len) as Vec3) : [0, 0, 0];
};

/**
 * Angle (radians) between two vectors, clamped against FP drift, 0 for a
 * degenerate input. (gl-matrix's vec3.angle returns π/2 for a zero vector.)
 */
export const angleBetween = (a: Vec3, b: Vec3): number => {
  const la = vec3.length(a);
  const lb = vec3.length(b);
  if (la < 1e-12 || lb < 1e-12) return 0;
  const c = Math.max(-1, Math.min(1, vec3.dot(a, b) / (la * lb)));
  return Math.acos(c);
};

/** Read vertex `i` (3-stride) from a positions array as a tuple. */
export const vertexAt = (positions: ArrayLike<number>, i: number): Vec3 => [
  positions[i * 3],
  positions[i * 3 + 1],
  positions[i * 3 + 2],
];
